import {
    IOnboardingSequence,
    ISessionConfig,
    IAIOnboardingStep
} from '~/types/server.types';
import { SpecialistData } from '~/packages/ai-core/types/server-types/structured-responses';
import { BaseHttpAdapter } from '~/services/adapters/base-http.adapter';
import { RootStore } from '~/stores/root.store';
import { trackError } from '~/services/logger/error.utils';
import { TIntentType } from '~/components/IntentHelpers/intent.utils';
import {
    IInvoiceIntentOnboardingService,
    ICreateLeadFormRequest,
    IOnboardingClient
} from '~/components/IntentOnboarding/types/IntentOnboarding.types';
import { IPaymentContent, IRecurringSchedule } from '~/types/model.types';
import { IFlowGalleryTemplate } from '~/components/FlowGallery/types/flow-gallery.types';
import {
    OnboardingStepNames,
    TSetupGuide
} from '~/components/IntentOnboarding/types/onboarding.types';
import { IAIOnboardingPipelineStages } from '~/components/AIChat/AIChatOnboarding/components/AIPipelinePreview/AIPipelinePreview.types';

export class OnboardingAdapter extends BaseHttpAdapter {
    rootStore: RootStore;

    async createOnboardingSmaf({
        onboardingType,
        createTemplateOnly = false,
        services = null,
        isToDeleteExistingServices = false,
        sessionConfigId = null,
        isRecurring = false,
        paymentBlocksContents = null,
        invoiceRecurringSchedule = null,
        cancellationAndReschedulingPolicy = null,
        newClientData = null,
        templateId = null,
        importRelationships = null
    }: {
        onboardingType: TIntentType;
        createTemplateOnly?: boolean;
        services?: Array<IInvoiceIntentOnboardingService>;
        isToDeleteExistingServices?: boolean;
        sessionConfigId?: string;
        isRecurring?: boolean;
        paymentBlocksContents?: IPaymentContent[];
        invoiceRecurringSchedule?: IRecurringSchedule;
        cancellationAndReschedulingPolicy?: string;
        newClientData?: IOnboardingClient;
        templateId?: string;
        importRelationships?: Array<IOnboardingClient>;
    }) {
        const {
            userStore: { currUser }
        } = this.rootStore;

        try {
            return await this.apiService.apiRequest<{ _id: string }>({
                method: 'post',
                url: `/api/v2/users/${currUser._id}/onboarding/create_onboarding_smaf`,
                data: {
                    selected_services: services,
                    session_config_id: sessionConfigId,
                    onboarding_type: onboardingType,
                    create_template_only: createTemplateOnly,
                    is_recurring: isRecurring,
                    payment_blocks_contents: paymentBlocksContents,
                    invoice_recurring_schedule: invoiceRecurringSchedule,
                    delete_existing_services: isToDeleteExistingServices,
                    rescheduling_and_cancellation_policy:
                        cancellationAndReschedulingPolicy,
                    create_client: newClientData,
                    template_id: templateId,
                    import_relationships: importRelationships
                }
            });
        } catch (error) {
            trackError({
                error,
                message: 'Create onboarding smart file failed',
                context: {
                    userId: currUser._id,
                    onboarding_type: onboardingType
                }
            });
            return null;
        }
    }

    async getFlowTemplates(serviceType: string) {
        const {
            userStore: { currUser }
        } = this.rootStore;

        return this.apiService.apiRequest<IFlowGalleryTemplate[]>({
            method: 'get',
            url: `/api/v2/users/${currUser._id}/onboarding_sequence/flow_templates`,
            params: {
                service_type: serviceType
            }
        });
    }

    async createOnboardingPuf(createLeadFormRequest: ICreateLeadFormRequest) {
        const {
            userStore: { currUser }
        } = this.rootStore;

        const response = await this.apiService.apiRequest<{
            flow_url: string;
            _id: string;
        }>({
            method: 'post',
            url: `/api/v2/users/${currUser._id}/onboarding/create_onboarding_puf`,
            data: createLeadFormRequest
        });
        return response;
    }

    async completeOnboardingStep(
        stepKey: OnboardingStepNames,
        isAISetupGuideEnabled: boolean
    ) {
        const {
            userStore: { currUser }
        } = this.rootStore;

        try {
            const response =
                await this.apiService.apiRequest<IOnboardingSequence>({
                    method: isAISetupGuideEnabled ? 'put' : 'post',
                    url: isAISetupGuideEnabled
                        ? `/api/v2/users/${currUser._id}/setup_sequence/${stepKey}`
                        : `/api/v2/users/${currUser._id}/onboarding_sequence/${stepKey}`
                });
            return response;
        } catch (error) {
            trackError({
                error,
                message: 'Complete onboarding step failed',
                context: {
                    userId: currUser._id,
                    stepKey,
                    isAISetupGuideEnabled
                }
            });
            return null;
        }
    }

    async completeOnboarding() {
        const { uiPersistenceStore } = this.rootStore;

        uiPersistenceStore.set(`homeCardSetupGuideStreamlined`, {
            hasMarkedComplete: true
        });
    }

    async saveServicesToElastic(flowId) {
        const response = await this.apiService.apiRequest<any>({
            method: 'put',
            url: `/api/v2/flow/${flowId}/save_flow_in_elastic`
        });

        return response;
    }

    async getSchedulerOnboardingSessionConfig() {
        const response = await this.apiService.apiRequest<ISessionConfig>({
            method: 'get',
            url: `/api/v2/users/scheduler_onboarding/session_config`
        });

        return response;
    }

    async extendTrial({
        daysToExtend,
        source
    }: {
        daysToExtend: number;
        source: string;
    }) {
        const {
            userStore: { currUser }
        } = this.rootStore;

        return this.apiService.apiRequest({
            method: 'put',
            url: `/api/v2/users/${currUser._id}/extend_trial/ex_trialer_modal`,
            data: {
                days_to_extend: daysToExtend,
                source_of_request: source
            }
        });
    }

    async updateSchedulerOnboardingSessionAndTimezone(data: {
        session_config_id: ISessionConfig['_id'];
        session_name: ISessionConfig['session_name'];
        session_type: ISessionConfig['session_type'];
        session_duration: ISessionConfig['session_duration'];
        session_timezone: ISessionConfig['session_timezone'];
        phone_call_caller: ISessionConfig['phone_call_caller'];
        video_conference_type: ISessionConfig['video_conference_type'];
    }) {
        const response = await this.apiService.apiRequest<ISessionConfig>({
            method: 'put',
            url: `/api/v2/users/scheduler_onboarding/session_config`,
            data: data
        });

        return response;
    }

    async getOnboardingSequence(userId: string) {
        return this.apiService.apiRequest<IOnboardingSequence>({
            method: 'get',
            url: `/api/v2/users/${userId}/onboarding_sequence`
        });
    }

    async getSetupGuideSequence(userId: string, setupType: TSetupGuide) {
        return this.apiService.apiRequest<
            IOnboardingSequence<IAIOnboardingStep>
        >({
            method: 'get',
            url: `/api/v2/users/${userId}/setup_sequence/${setupType}`
        });
    }

    /* ------------------- AI ONBOARDING PIPELINE STAGES ------------------- */
    getAIOnboardingPipelineStages({ viewId }: { viewId: string }) {
        return this.apiService.apiRequest<IAIOnboardingPipelineStages>({
            method: 'get',
            url: `/api/v2/workspaces/pipeline/views/${viewId}/stages`
        });
    }

    async getSpecialistData(id: string) {
        const response = await this.apiService.apiRequest<{
            _id: string;
            notes: SpecialistData;
        }>({
            method: 'get',
            url: `/api/v2/onboarding/specialist_data/${id}`
        });
        return response.notes;
    }
}
